// assistantReveal.js — A NEW ANSWER'S SECTION COMES TO THE TOP (Nika
// 2026-10-23: "when answering in new section always scroll top of that
// section"). Every main-area view that answers in a new section brings that
// section's top under the shell's floating header, once, when it starts:
//   Main       the new Fly page (functions/assistantFly.js assistantFlyReveal)
//   Chat/Build the question just asked — its answer reads down from it, never
//              a chase of the stream's bottom (components/AssistantMainThread.js)
//   Present    the deck (components/AssistantDeckView.js)
//   Dashboard  the generated board (components/AssistantBoardView.js)
//
// THE ROOM. A new section is the LAST thing in its view: a question just
// asked has no answer under it yet, so the scroller cannot lift it to the top
// (measured on dev 2026-10-23: the question stopped at 409px of a 963px
// view, under 75px wanted, and its answer streamed down under the composer).
// The reveal measures the shortfall and gives the view that much room: a
// `min-height` that holds the section at the top while its answer fills the
// room in, and stops counting once the answer is taller than the view. The
// room lives in root state (`_revealRoom`: the view it belongs to and its
// height) and each view reads it in its own `minHeight` while it is on
// screen — one view holds the main area at a time, so one room is enough.
//
// The scroll follows the scroller's own CSS `scroll-behavior` (the brand reset
// turns smooth scrolling off under reduced motion) and the section's own
// `scroll-margin-top` (the header's clearance). The section's place is read
// from its layout box: a page that rises in (`fadeInUpShort`) starts lower by
// its travel, and the top read through that offset left a Fly page's head
// 12px under the header's clearance (63px, not 75px; measured on dev the
// same day). A section can render a beat after the state that made it (its
// view's children follow the same update): the reveal waits a few short
// ticks for its node, then gives up. The section and its scroller are found
// through the element tree, never a DOM query.
//
// FRANK: every export is self-contained; siblings through `this.call`.

// `key`: the view's descendant keyed so (a page, a row), else the view itself.
// `mark`: once per mark per view (a page key, a question's key, a request).
export const assistantRevealTop = function assistantRevealTop(el, key, mark, tries) {
  const host = el || this
  const id = String(mark == null ? key || '' : mark)
  if (!host || !id || host.__revealedTop === id) return false
  const target = key ? (typeof host.lookdown === 'function' ? host.lookdown(key) : null) : host
  const node = target && target.node
  if (!node || node.isConnected === false || typeof node.getBoundingClientRect !== 'function') {
    const n = Number(tries) || 0
    if (n >= 10) return false
    const again = this
    setTimeout(() => again.call('assistantRevealTop', host, key, mark, n + 1), 40)
    return false
  }
  host.__revealedTop = id
  // Measure, give the view the room it lacks, scroll. The room reaches the
  // view through its reactive `minHeight`: when the scroller cannot go that
  // far yet (the write lands a tick later, or a closed view's box below
  // absorbed part of the growth), measure again on the next tick — three
  // passes at most, then scroll as far as it goes.
  const settle = (pass) => {
    const box = this.call('assistantRevealMeasure', host, target)
    const plan = box ? this.call('assistantRevealPlan', box) : null
    if (!plan) {
      // no scroller to read (a test document): the browser's own reveal
      if (typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'start' })
      return
    }
    const scroller = box.scroller
    if (plan.room > 0 && pass < 3) {
      this.call('assistantRevealRoomSet', host, plan.room)
      const reach = Number(scroller.scrollHeight) - Number(scroller.clientHeight)
      if (reach + 0.5 < plan.top) {
        setTimeout(() => settle(pass + 1), 0)
        return
      }
    }
    if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ top: plan.top })
    else scroller.scrollTop = plan.top
  }
  settle(0)
  return true
}

// The numbers the reveal needs, read from the layout: where the section
// stands in its scroller (its layout box — a running rise-in's offset taken
// out), the header's clearance (`scroll-margin-top`), how far the scroller
// can go, and the view's own height (its content box, which `min-height`
// sizes). Null without a scroller to read.
export const assistantRevealMeasure = function assistantRevealMeasure(host, target) {
  const node = target && target.node
  const hostNode = host && host.node
  const doc = node && node.ownerDocument
  const win = doc && doc.defaultView
  if (!node || !hostNode || !win || typeof win.getComputedStyle !== 'function') return null
  const scroller = this.call('assistantRevealScroller', target)
  if (!scroller || typeof scroller.getBoundingClientRect !== 'function') return null
  const css = win.getComputedStyle(node)
  const lift = (t) => {
    const m = /^matrix(3d)?\((.+)\)$/.exec(String(t || ''))
    if (!m) return 0
    const v = m[2].split(',').map(Number)
    return (m[1] ? v[13] : v[5]) || 0
  }
  const hostCss = win.getComputedStyle(hostNode)
  const px = (v) => parseFloat(v) || 0
  const frame =
    hostCss.boxSizing === 'border-box'
      ? 0
      : px(hostCss.paddingTop) +
        px(hostCss.paddingBottom) +
        px(hostCss.borderTopWidth) +
        px(hostCss.borderBottomWidth)
  const root = scroller === doc.scrollingElement
  return {
    scroller,
    nodeTop: node.getBoundingClientRect().top - lift(css.transform),
    clearance: px(css.scrollMarginTop),
    scrollerTop: root
      ? 0
      : scroller.getBoundingClientRect().top + (Number(scroller.clientTop) || 0),
    scrollTop: Number(scroller.scrollTop) || 0,
    scrollHeight: Number(scroller.scrollHeight) || 0,
    clientHeight: Number(scroller.clientHeight) || 0,
    hostHeight: Math.max(0, (Number(hostNode.offsetHeight) || 0) - frame)
  }
}

// Where to scroll, and the room the view needs for it: the section's top at
// the header's clearance; when the scroller cannot go that far, the view
// grows by the shortfall. `room` 0 keeps whatever room the view holds.
export const assistantRevealPlan = function assistantRevealPlan(m) {
  if (!m) return null
  const top = Math.max(0, m.scrollTop + m.nodeTop - m.scrollerTop - m.clearance)
  const short = top - Math.max(0, m.scrollHeight - m.clientHeight)
  return { top: Math.round(top), room: short > 0.5 ? Math.ceil(m.hostHeight + short) : 0 }
}

// The nearest ancestor that scrolls (its CSS says so), up the element tree;
// the document's scroller when none does.
export const assistantRevealScroller = function assistantRevealScroller(target) {
  const node = target && target.node
  const doc = node && node.ownerDocument
  const win = doc && doc.defaultView
  if (!win || typeof win.getComputedStyle !== 'function') return null
  for (let p = target.parent; p; p = p.parent) {
    const n = p.node
    if (!n || n.nodeType !== 1) continue
    const y = win.getComputedStyle(n).overflowY
    if (y === 'auto' || y === 'scroll' || y === 'overlay') return n
  }
  return doc.scrollingElement || null
}

// One view's name for its room: its place in the tree (the same view is
// mounted on the home and on the page layer).
export const assistantRevealViewId = function assistantRevealViewId(el) {
  const path = el && typeof el.getPath === 'function' ? el.getPath() : null
  return Array.isArray(path) && path.length ? path.join('.') : String((el && el.key) || '')
}

// The room a view needs (root state `_revealRoom`).
export const assistantRevealRoomSet = function assistantRevealRoomSet(el, px) {
  const root = el && typeof el.getRootState === 'function' ? el.getRootState() : null
  if (!root || typeof root.update !== 'function' || !(px > 0)) return false
  const view = this.call('assistantRevealViewId', el)
  root.update({ _revealRoom: { view, px: Math.ceil(px) } }, { preventFetch: true })
  return true
}

// The view's room goes (another conversation, a new session): only its own.
export const assistantRevealRoomClear = function assistantRevealRoomClear(el) {
  const root = el && typeof el.getRootState === 'function' ? el.getRootState() : null
  const room = root && root._revealRoom
  if (!room || typeof root.update !== 'function') return false
  if (room.view !== this.call('assistantRevealViewId', el)) return false
  root.update({ _revealRoom: null }, { preventFetch: true })
  return true
}

// A view's `minHeight`: its room, while it is on screen; `auto` (the
// property's own initial value) otherwise — a closed view keeps its box, and
// a room there would stretch the scroller under the view that is open. Never
// `null`: a reactive CSS prop that turns null keeps the inline value it last
// wrote (smbls element core, the CSS-prop effect blanks only show/hide —
// ticket FW-REACTIVE-CSS-PROP-NULL-KEEPS-STALE-INLINE-1), so the room would
// outlive its conversation.
export const assistantRevealRoom = function assistantRevealRoom(el, root, open) {
  const room = root && root._revealRoom
  if (!open || !room || !(room.px > 0)) return 'auto'
  if (room.view !== this.call('assistantRevealViewId', el)) return 'auto'
  return room.px + 'px'
}

// The thread's questions: how many it holds, the row key of the last one —
// the key the thread's rows carry (functions/assistantMainThread.js
// assistantThreadRows: the message id, else its place in the list) — and the
// first message's key, the conversation's identity (another conversation, a
// new chat). A new conversation's real id arrives only when its first turn
// ends ("onDone re-points activeSessionId"), so that id is no identity here.
export const assistantThreadAsks = function assistantThreadAsks(root) {
  const r = root && typeof root === 'object' ? root : {}
  const list = Array.isArray(r.chatMessages)
    ? r.chatMessages.filter((m) => m && typeof m === 'object')
    : []
  let count = 0
  let key = null
  list.forEach((m, i) => {
    if (m.role !== 'user') return
    count += 1
    key = String(m.id || 'm' + i)
  })
  const first = list.length ? String(list[0].id || 'm0') : null
  return { count, key, first }
}
